"use client";

import React from "react";
import { ArrowRight } from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const services = [{
  title: "Residential Basement & Attic Cleanout",
  description: "Reclaim your living space in Kanata or Orleans. We handle the heavy lifting and eco-friendly sorting of years of accumulated clutter.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672912/img/ottawa_basement_cleanout.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672912/img/ottawa_basement_cleanout.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672912/img/ottawa_basement_cleanout.png-wvc-sizes",
  availability: "Same-day available"
}, {
  title: "Commercial Office Space Removal",
  description: "Efficient decommissioning for Nepean businesses. We remove desks, electronics, and partitions with minimal disruption to your operations.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672912/img/commercial_office_junk_removal_ottawa.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672912/img/commercial_office_junk_removal_ottawa.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672912/img/commercial_office_junk_removal_ottawa.png-wvc-sizes",
  availability: "Same-day available"
}, {
  title: "Professional Furniture Removal",
  description: "From heavy sectional sofas in Barrhaven to old dining sets in Stittsville, our team ensures damage-free removal from any floor.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672912/img/furniture_removal_service_ottawa.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672912/img/furniture_removal_service_ottawa.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672912/img/furniture_removal_service_ottawa.png-wvc-sizes",
  availability: "Same-day available"
}, {
  title: "Old Appliance Disposal",
  description: "Safe extraction and certified recycling of fridges, washers, and dryers across Gloucester. We ensure all refrigerants are handled properly.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672912/img/appliance_disposal_ottawa_homes.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672912/img/appliance_disposal_ottawa_homes.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672912/img/appliance_disposal_ottawa_homes.png-wvc-sizes",
  availability: "Same-day available"
}, {
  title: "Compassionate Estate Cleanouts",
  description: "Respectful and thorough property clearing services in Rockland and Carp. We work closely with families to manage transitions smoothly.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672912/img/estate_cleanout_services_ottawa.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672912/img/estate_cleanout_services_ottawa.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672912/img/estate_cleanout_services_ottawa.png-wvc-sizes",
  availability: "Same-day available"
}, {
  title: "Yard Waste & Debris Removal",
  description: "Clear out branches, brush, and seasonal debris from your Ottawa property. Perfect for post-storm cleanup or landscaping preparation.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672912/img/yard_waste_removal_ottawa_suburbs.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672912/img/yard_waste_removal_ottawa_suburbs.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672912/img/yard_waste_removal_ottawa_suburbs.png-wvc-sizes",
  availability: "Same-day available"
}];
export default function OurInsectControlServices() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: gridRef,
    isVisible: isGridVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="259626" id="our-service-categories" className="bg-background text-foreground py-16 md:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div ref={headerRef} className={`mb-12 text-center transition-all duration-700 ease-out ${isHeaderVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
          <h2 className="font-serif text-foreground mb-4 text-3xl font-bold tracking-tight sm:text-4xl">
            Our Service Categories
          </h2>
          <p className="text-muted-foreground font-sans mx-auto max-w-2xl text-lg">
            Professional junk removal solutions engineered to clear your space with precision and local Ottawa care.
          </p>
        </div>

        <div ref={gridRef} className={`grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3 transition-all duration-1000 ease-out ${isGridVisible ? "translate-y-0 opacity-100" : "translate-y-12 opacity-0"}`}>
          {services.map((service, i) => {
          return <Card key={i} data-index={i} className="bg-card border-border/50 rounded-lg shadow-lg transition-all duration-300 hover:scale-[1.02] hover:shadow-3xl hover:border-primary/50 overflow-hidden">
                <img src={service.image} data-wvc-srcset={service.srcset} data-wvc-sizes={service.sizes} alt={service.title} className="w-full h-48 object-cover" loading="lazy" decoding="async" />
                <CardHeader className="pt-6">
                  <CardTitle className="font-serif text-foreground text-xl tracking-tight">{service.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="font-sans text-muted-foreground text-base mb-4 leading-relaxed">
                    {service.description}
                  </CardDescription>
                  
                  <div className="mb-6">
                    <span className="inline-flex items-center rounded-lg bg-secondary px-2.5 py-0.5 text-xs font-bold text-secondary-foreground uppercase tracking-wider">
                      {service.availability}
                    </span>
                  </div>

                  <Button variant="link" asChild className="text-secondary hover:text-secondary/80 p-0 h-auto font-bold uppercase tracking-wider text-xs">
                    <Link to="/ServicesPage">
                      Book Now
                      <ArrowRight className="ml-2 h-3 w-3" />
                    </Link>
                  </Button>
                </CardContent>
              </Card>;
        })}
        </div>

        <div className="mt-16 flex justify-center">
          <Button asChild size="lg" variant="secondary" className="rounded-lg px-8 py-7 text-lg font-bold shadow-xl hover:scale-105 transition-transform">
            <Link to="/PricingPage">
              Schedule an Inspection
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}